// The quiet "Account" link in the Focus top bar (the picker and the main menu),
// and the "Open account" link in the picker's lapsed-connection hint.
//
// No `fetchMe` gate, unlike `AdminNavLink`: every surface that mounts it is
// already behind `requireApproved`, and the page always has something on it
// (who is signed in, Sign out). It hands the current path to the page as
// `?from=`, so the page's Back returns here. A page whose URL carries state
// Back must keep (the meetings page's `?project=`) passes its own `from`.
//
// `useRouter({ warn: false })`, as `useHereLocation` does: Focus renders in
// component tests that mount no router. With none, it falls back to a full
// navigation. In the Focus panel (`src/panel/`) it opens Loom's account page
// in the browser.

import { useRouter } from "@tanstack/react-router";

import { useHereLocation } from "../focus/figma/useHereLocation";
import { useT, type MessageKey } from "../i18n/index.js";
import { isPanelHost, openLoomPage } from "../lib/host";

import { accountHref } from "./accountLinks";

export function AccountNavLink({
  label = "account.navLabel",
  from,
}: {
  readonly label?: MessageKey;
  /** Where the account page's Back returns; the current path by default. */
  readonly from?: string;
}) {
  const t = useT();
  const router = useRouter({ warn: false }) as
    | ReturnType<typeof useRouter>
    | undefined;
  const { here } = useHereLocation();
  const open = (): void => {
    // The Focus panel has no account page of its own: Loom's opens in the
    // browser, where the page's Back leads nowhere, so no `?from=`.
    if (isPanelHost()) {
      openLoomPage("/account");
      return;
    }
    const href = accountHref(from ?? here.pathname);
    if (router !== undefined) {
      void router.navigate({ href });
      return;
    }
    window.location.assign(href);
  };
  return (
    <button type="button" className="focus-deeplink" onClick={open}>
      {t(label)}
    </button>
  );
}
